import { Component, DestroyRef, inject, OnInit } from '@angular/core';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { HttpHapService } from '@bo-client/core/http/http-hap.service';
import { DataShareService } from '@bo-client/shared/services/data-share.service';
import { Router } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import { FormBuilder, FormControl, Validators } from '@angular/forms';
import { GisDataModel } from '@bo-client/shared/models/gisData';
import { distinctUntilChanged, map, shareReplay, startWith, tap } from 'rxjs/operators';
import { CreateFormsService } from '@bo-client/activation-management/shared/forms/create-forms.service';
import {
  ActionTypeEnum,
  AreaTypeDisplayName,
  AreaTypeEnum
} from '@bo-client/activation-management/shared/models/commissioningModel';
import {
  AnswerWithMoreInfoModel,
  NavigateToEnum
} from '@bo-client/activation-management/shared/models/AnswerWithMoreInfoModel';
import { path } from '@bo-client/shared/constants/path';
import { GisDataEvent } from '@bo-client/shared/components/get-gis-data/get-gis-data.component';
import {
  ExternalCommissioningRequestModel,
  MeterInspectionForm
} from '@bo-client/activation-management/shared/models/ExternalCommissioningRequestModel';
import { CompanyLocationDto } from '@bo-client/shared/models/companyLocations';
import { uri } from '@bo-client/shared/constants/uri';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { LoadDataFormsService } from '@bo-client/activation-management/commissioning/forms/load-data-forms.service';
import { minDateFromValidator } from '@bo-client/shared/forms/forms.validators';

@Component({
  selector: 'app-meter-inspection-page',
  templateUrl: './meter-inspection-page.component.html',
  styleUrls: ['./meter-inspection-page.component.scss']
})
export class MeterInspectionPageComponent implements OnInit {
  private fb = inject(FormBuilder);
  private httpHapService = inject(HttpHapService);
  private dataShareService = inject(DataShareService);
  private router = inject(Router);
  private createFormsService = inject(CreateFormsService);
  private loadDataFormsService = inject(LoadDataFormsService);
  private toastService = inject(ToastrService);
  private destroyRef = inject(DestroyRef);

  formGroup$: Observable<MeterInspectionForm>;
  summary$: Observable<ExternalCommissioningRequestModel>;

  companyLocationsByTenant$: Observable<CompanyLocationDto[]>;
  /** Sparten, für die dieser Mandant überhaupt Betriebsbereiche hat. */
  availableAreaTypes$: Observable<AreaTypeEnum[]>;
  /** Steuert den zusätzlichen Schritt für den Durchführungs-Wunschtermin. */
  supplyWishDateActive$: Observable<boolean>;
  eventsFromParent$ = of('');
  selectedConnectionObject$: BehaviorSubject<GisDataModel> = new BehaviorSubject(null);
  firstDateAvailable: Date = new Date();

  /** Sparten, für die eine Befundprüfung beantragt werden kann — in kanonischer Reihenfolge. */
  private static readonly inspectionAreaTypes: AreaTypeEnum[] = [
    AreaTypeEnum.GAS,
    AreaTypeEnum.POWER,
    AreaTypeEnum.WATER,
    AreaTypeEnum.DISTRICTHEAT
  ];

  ngOnInit(): void {
    this.companyLocationsByTenant$ = this.httpHapService
      .getTyped<CompanyLocationDto[]>(uri.companyLocationByCurrentTenant)
      .pipe(shareReplay(1));
    this.availableAreaTypes$ = this.companyLocationsByTenant$.pipe(
      map((companyLocations) => this.collectAvailableAreaTypes(companyLocations)),
      shareReplay(1)
    );
    this.firstDateAvailable = this.loadDataFormsService.setFirstAvailableDate(
      this.dataShareService.selectedTenant.supplyAppointmentDays
    );
    this.supplyWishDateActive$ = this.dataShareService
      .getSingleTenantConfig$<boolean>('commissioning.inspectionConfig.activateSupplyWishDate', false)
      .pipe(takeUntilDestroyed(this.destroyRef), shareReplay(1));

    this.formGroup$ = this.supplyWishDateActive$.pipe(
      map((supplyWishDateActive) => this.createFormGroup(supplyWishDateActive)),
      tap((formGroup) => {
        this.resetConnectionObjectOnAreaChange(formGroup);
        this.summary$ = formGroup.valueChanges.pipe(
          startWith(formGroup.value),
          distinctUntilChanged(),
          map((formValue) => this.formValueToDto(formValue)),
          shareReplay(1)
        );
      }),
      shareReplay(1)
    );
  }

  /**
   * Sparten, die an mindestens einem Betriebsbereich des Mandanten geführt werden. Hat der Mandant
   * keine Sparten hinterlegt, werden alle Sparten angeboten — die Verfügbarkeit an der konkreten
   * Adresse prüft ohnehin der Anschlussobjekt-Schritt.
   */
  private collectAvailableAreaTypes(companyLocations: CompanyLocationDto[]): AreaTypeEnum[] {
    const availableNames = new Set<string>();
    (companyLocations || []).forEach((companyLocation) =>
      (companyLocation.areatypes || []).forEach((areaType) => availableNames.add(areaType.name))
    );
    const available = MeterInspectionPageComponent.inspectionAreaTypes.filter((areaType) =>
      availableNames.has(areaType)
    );
    return available.length ? available : MeterInspectionPageComponent.inspectionAreaTypes;
  }

  /**
   * Nach einem Spartenwechsel muss das Anschlussobjekt erneut geprüft werden — die vorige Sparte ist
   * an der gewählten Adresse nicht zwangsläufig auch die neue.
   */
  private resetConnectionObjectOnAreaChange(formGroup: MeterInspectionForm): void {
    formGroup.controls.area.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      this.selectedConnectionObject$.next(null);
      formGroup.controls.connectionObjectValid.setValue(false);
    });
  }

  private createFormGroup(supplyWishDateActive: boolean): MeterInspectionForm {
    const formGroup = this.fb.group({
      area: [null as AreaTypeEnum | null, Validators.required],
      customer: this.fb.array([this.createFormsService.createContactDataForm({})]),
      connectionObject: this.createFormsService.connectionObjectFormWithValidator({}, null),
      connectionObjectValid: [false, Validators.requiredTrue],
      supplyAppointment: this.createFormsService.supplyAppointment({
        supplyAppointment: this.firstDateAvailable,
        supplyAppointmentWishDate: null,
        supplyAppointmentWishPartday: null
      }),
      technicalData: this.fb.group({
        meterNumber: ['', Validators.required],
        exceedingExpectedConsumption: [false],
        causesNoise: [false],
        comment: ['']
      }),
      furtherInformation: this.fb.group({
        permissionToOpenDevice: [null as boolean | null, Validators.required],
        requestsAttendance: [false],
        acceptCostCondition: [false, Validators.requiredTrue],
        acceptCommissionCondition: [false, Validators.requiredTrue]
      }),
      acceptConditions: [false, Validators.requiredTrue]
    }) as MeterInspectionForm;

    // Der Wunschtermin ist nur dann Pflicht, wenn der Schritt auch angezeigt wird.
    if (supplyWishDateActive) {
      const { supplyAppointmentWishDate, supplyAppointmentWishPartday } =
        formGroup.controls.supplyAppointment.controls;
      supplyAppointmentWishDate.setValidators([
        Validators.required,
        minDateFromValidator(this.firstDateAvailable)
      ]);
      supplyAppointmentWishPartday.setValidators([Validators.required]);
    }

    return formGroup;
  }

  submit(formGroup: MeterInspectionForm) {
    if (formGroup.invalid) {
      console.debug('Befundprüfung form is invalid', formGroup, formGroup.value);
      formGroup.markAllAsTouched();
      if (formGroup.controls.acceptConditions.invalid) {
        this.toastService.error('Bitte akzeptieren Sie die Bedingungen.');
      }
      return;
    }

    const data = this.formValueToDto(formGroup.value);
    this.httpHapService
      .postTyped<AnswerWithMoreInfoModel>(data, '/external/commissioningrequest/inspection')
      .subscribe({
        next: (result) => {
          this.toastService.success('Vielen Dank, wir haben Ihre Daten erhalten.');
          this.navigateTo(result);
        },
        error: (err) => {
          console.error('Fehler beim Versenden des Antrags auf Befundprüfung', err);
          this.toastService.error('Beim Übermitteln ist ein Fehler aufgetreten: ' + err);
        }
      });
  }

  onGisDataChanged(
    $event: GisDataEvent & { currentCompanyLocationFromGis: CompanyLocationDto },
    validityControl: FormControl<boolean>
  ) {
    this.selectedConnectionObject$.next($event.gisData);
    validityControl.setValue($event.areaAvailable);
  }

  navigateTo(answer: AnswerWithMoreInfoModel) {
    if (answer.navigateTo !== NavigateToEnum.none) {
      this.router
        .navigate([path.external + '/' + answer.navigateTo + '/' + answer.message], {
          replaceUrl: true
        })
        .then();
    }
  }

  get netOperatorName(): string {
    return this.dataShareService.selectedTenant?.displayName || '';
  }

  private formValueToDto(formValue: MeterInspectionForm['value']): ExternalCommissioningRequestModel {
    const gisData = this.selectedConnectionObject$.getValue();
    const requestModel = new ExternalCommissioningRequestModel(
      ActionTypeEnum.inspection,
      null,
      formValue.area
    );
    const { customer, technicalData, furtherInformation } = formValue;
    requestModel.customer = customer[0] || {};
    requestModel.actions[0].meterNumber = technicalData?.meterNumber || '';
    requestModel.supplyAppointment = formValue.supplyAppointment?.date ?? null;
    requestModel.supplyAppointmentWishDate = formValue.supplyAppointment?.supplyAppointmentWishDate ?? null;
    requestModel.supplyAppointmentWishPartday =
      formValue.supplyAppointment?.supplyAppointmentWishPartday ?? null;
    requestModel.actions[0].inspection = {
      exceedingExpectedConsumption: technicalData?.exceedingExpectedConsumption || false,
      causesNoise: technicalData?.causesNoise || false,
      comment: technicalData?.comment || undefined,
      permissionToOpenDevice: furtherInformation?.permissionToOpenDevice ?? undefined,
      requestsAttendance: furtherInformation?.requestsAttendance || false
    };
    const { houseNo, houseNoAddition, ...rest } = gisData || {};
    requestModel.companyLocation.external_id = rest.orgEinheit;
    requestModel.connectionObject = { ...rest, streetNo: houseNo, streetNoAddition: houseNoAddition };
    return requestModel;
  }

  protected readonly AreaTypeDisplayName = AreaTypeDisplayName;
}
